import React from "react";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const AddStudentDialog = ({ open, onOpenChange, form, setForm, tshirtSizes = [], onSubmit }) => {
  const activeSizes = (tshirtSizes || []).filter((s) => s.is_active);
  const selectedInactive = (tshirtSizes || []).find(
    (s) => !s.is_active && s.size_id === form.tshirt_size_id
  );

  // Derive age from DOB when provided; otherwise honour the manual selection.
  const dobDerivedAge = React.useMemo(() => {
    if (!form.date_of_birth) return null;
    const dob = new Date(form.date_of_birth);
    if (isNaN(dob.getTime())) return null;
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) years -= 1;
    return years >= 0 && years <= 120 ? years : null;
  }, [form.date_of_birth]);

  // Keep form.age in sync with DOB-derived age so the submit payload always carries it.
  React.useEffect(() => {
    if (dobDerivedAge != null && String(form.age) !== String(dobDerivedAge)) {
      setForm((prev) => ({ ...prev, age: dobDerivedAge }));
    }
  }, [dobDerivedAge, form.age, setForm]);

  const ageLocked = dobDerivedAge != null;
  const ageMissing = !form.age || Number(form.age) < 1 || Number(form.age) > 99;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Add Student</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="add-student-name"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Full name"
                className="h-11 rounded-xl"
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Mobile Number *</Label>
              <Input
                data-testid="add-student-phone"
                value={form.phone}
                onChange={(e) => {
                  const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                  setForm({...form, phone: digits});
                }}
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                placeholder="10-digit mobile"
                className="h-11 rounded-xl"
              />
              {form.phone && form.phone.length !== 10 && (
                <p data-testid="add-student-phone-hint" className="text-xs text-red-600">
                  Mobile number must be exactly 10 digits
                </p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Email</Label>
              <Input
                data-testid="add-student-email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="Email address"
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <select
                value={form.gender}
                onChange={(e) => setForm({...form, gender: e.target.value})}
                className="w-full h-11 rounded-xl border px-3 bg-white"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({...form, date_of_birth: e.target.value})}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Age <span className="text-red-500">*</span></Label>
              <select
                data-testid="add-student-age"
                value={form.age ?? ""}
                onChange={(e) => setForm({...form, age: e.target.value ? Number(e.target.value) : ""})}
                disabled={ageLocked}
                className="w-full h-11 rounded-xl border px-3 bg-white disabled:bg-slate-50 disabled:text-slate-500"
              >
                <option value="">{ageLocked ? `${dobDerivedAge} (from DOB)` : "Select age"}</option>
                {Array.from({ length: 99 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              {ageLocked && (
                <p className="text-[10px] text-slate-500">Auto-calculated from DOB. Clear DOB to enter manually.</p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({...form, address: e.target.value})}
                placeholder="Full address"
                className="h-11 rounded-xl"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">Emergency Contact</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={form.emergency_contact_name}
                  onChange={(e) => setForm({...form, emergency_contact_name: e.target.value})}
                  placeholder="Name"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  data-testid="add-student-emergency-phone"
                  value={form.emergency_contact_phone}
                  onChange={(e) => {
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                    setForm({...form, emergency_contact_phone: digits});
                  }}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  placeholder="10-digit mobile"
                  className="h-11 rounded-xl"
                />
                {form.emergency_contact_phone && form.emergency_contact_phone.length !== 10 && (
                  <p className="text-xs text-red-600">Phone must be exactly 10 digits</p>
                )}
              </div>
            </div>
          </div>

          {(activeSizes.length > 0 || selectedInactive) && (
            <div className="space-y-2">
              <Label>T-Shirt Size <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <select
                data-testid="add-student-tshirt-size"
                value={form.tshirt_size_id || ""}
                onChange={(e) => setForm({...form, tshirt_size_id: e.target.value})}
                className="w-full h-11 rounded-xl border border-slate-200 px-3 bg-white"
              >
                <option value="">— Not specified —</option>
                {activeSizes.map((s) => (
                  <option key={s.size_id} value={s.size_id}>{s.label}</option>
                ))}
                {selectedInactive && (
                  <option key={selectedInactive.size_id} value={selectedInactive.size_id}>
                    {selectedInactive.label} (Inactive)
                  </option>
                )}
              </select>
            </div>
          )}

          <Button
            data-testid="confirm-add-student"
            onClick={onSubmit}
            disabled={
              !form.name.trim() ||
              !form.phone.trim() ||
              form.phone.length !== 10 ||
              ageMissing ||
              (form.emergency_contact_phone && form.emergency_contact_phone.length !== 10)
            }
            className="w-full h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Add Student
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentDialog;
